import { useTranslation } from "react-i18next";

import NotificationPreferenceRow, {
    type NotificationAudience,
} from "../../../../shared/ui/notifications/NotificationPreferenceRow";
import { useCenter } from "../../../center";
import { useReminderChannels, ReminderChannelsNotice } from "../../../notification";
import { useUpdateTherapist } from "../../../therapist";
import type { Therapist } from "../../../therapist";

/**
 * Every message Payments sends, who receives each one, and how.
 *
 * Three kinds: a payment request, a change to a payment (paid, or withdrawn
 * after the patient was told), and the automatic reminders. Reminders keep the
 * switch they always had — `payment_reminders_enabled`, which a centre decides
 * for its own patients — and gain the choice of recipients.
 *
 * Requests and reminders offer the channel of the patient's copy where this
 * deployment can send them on WhatsApp; a change to a payment has no WhatsApp
 * message at all, so it states email. Each row carries a one-line summary.
 *
 * The flags come from the server, which holds their defaults.
 *
 * Saves on change, like the Scheduler's notifications: each control stands on
 * its own, so nothing needs holding back behind a Save button.
 */
const PaymentNotificationSettings: React.FC = () => {
    const { t } = useTranslation();
    const { therapist, update: save, saving, failed } = useUpdateTherapist();
    const { center } = useCenter();
    // One request for the whole section: the rows and the notice read the same answer.
    const channels = useReminderChannels();

    if (!therapist) return null;

    const audience =
        (toPatient: keyof Therapist, toTherapist: keyof Therapist) =>
        (chosen: NotificationAudience) =>
            save({ [toPatient]: chosen.toPatient, [toTherapist]: chosen.toTherapist });

    return (
        <section className="flex flex-col gap-4">
            <div>
                <h3 className="text-base font-semibold text-content-primary">
                    {t("payments.settings.notifications.title")}
                </h3>
                <p className="text-sm text-content-tertiary">
                    {t("payments.settings.notifications.description")}
                </p>
            </div>

            <NotificationPreferenceRow
                name="payment-request"
                title={t("payments.settings.notifications.request.title")}
                description={t("payments.settings.notifications.request.hint")}
                enabled={therapist.payment_request_notice_enabled}
                toPatient={therapist.payment_request_notice_to_patient}
                toTherapist={therapist.payment_request_notice_to_therapist}
                disabled={saving}
                onEnabledChange={(enabled) =>
                    save({ payment_request_notice_enabled: enabled })
                }
                onAudienceChange={audience(
                    "payment_request_notice_to_patient",
                    "payment_request_notice_to_therapist"
                )}
                channel={{
                    kind: "choice",
                    value: therapist.payment_request_notice_channel,
                    whatsappAvailable: channels.whatsapp_notices.payment_request,
                    onChange: (payment_request_notice_channel) =>
                        save({ payment_request_notice_channel }),
                }}
                when={{ key: "payments.settings.notifications.request.when" }}
            />

            <NotificationPreferenceRow
                name="payment-change"
                title={t("payments.settings.notifications.change.title")}
                description={t("payments.settings.notifications.change.hint")}
                enabled={therapist.payment_change_notice_enabled}
                toPatient={therapist.payment_change_notice_to_patient}
                toTherapist={therapist.payment_change_notice_to_therapist}
                disabled={saving}
                onEnabledChange={(enabled) =>
                    save({ payment_change_notice_enabled: enabled })
                }
                onAudienceChange={audience(
                    "payment_change_notice_to_patient",
                    "payment_change_notice_to_therapist"
                )}
                // No WhatsApp message exists for paid or cancelled, so there is
                // nothing to choose — and no field to send.
                channel={{ kind: "fixedEmail" }}
                when={{ key: "payments.settings.notifications.change.when" }}
            />

            <NotificationPreferenceRow
                name="payment-reminder"
                title={t("payments.settings.notifications.reminder.title")}
                // The manual "send reminder" button keeps working either way:
                // someone pressing it is making the decision themselves.
                description={t("payments.settings.remindersHint")}
                // Predates the choice of recipients, and stays optional.
                enabled={therapist.payment_reminders_enabled !== false}
                toPatient={therapist.payment_reminder_to_patient}
                toTherapist={therapist.payment_reminder_to_therapist}
                disabled={saving}
                onEnabledChange={(enabled) => save({ payment_reminders_enabled: enabled })}
                onAudienceChange={audience(
                    "payment_reminder_to_patient",
                    "payment_reminder_to_therapist"
                )}
                channel={{
                    kind: "choice",
                    value: therapist.payment_reminder_notice_channel,
                    whatsappAvailable: channels.whatsapp_notices.payment_reminder,
                    onChange: (payment_reminder_notice_channel) =>
                        save({ payment_reminder_notice_channel }),
                }}
                when={{ key: "payments.settings.notifications.reminder.when" }}
            >
                {center && (
                    <p className="text-sm text-content-tertiary">
                        {t("payments.settings.notifications.reminder.centerNote", {
                            center: center.name,
                        })}
                    </p>
                )}
            </NotificationPreferenceRow>

            {/* Under every row rather than inside one: it answers "how does
                this reach them" for the request and the chasing alike. */}
            <ReminderChannelsNotice kind="payment" channels={channels} />

            {failed && (
                <p role="alert" className="text-sm text-red-600">
                    {t("payments.settings.saveError")}
                </p>
            )}
        </section>
    );
};

export default PaymentNotificationSettings;
